import _ from "lodash";
import {
  Accordion,
  AccordionSummary,
  Typography,
  AccordionDetails,
  FormControlLabel,
  FormGroup,
  Checkbox,
  CircularProgress,
} from "@material-ui/core";
import React, {useState} from "react";
import {DocumentAmendment} from "common/flowmaster/types/amendment";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import CheckIcon from "@material-ui/icons/Check";
import CrossIcon from "@material-ui/icons/Close";
import WarningIcon from "@material-ui/icons/Warning";

export interface AmendmentHistory {
  amendment: DocumentAmendment;
  hasFailed: boolean;
  reason?: string;
  subHistory?: AmendmentHistory[];
}

function getMessage(isApplying, progress, fails, partialFails, total) {
  if (isApplying) {
    return `Applying Amendments (${progress}/${total})`;
  }

  if (fails === 0 && partialFails === 0) {
    return `Successfully applied ${total} Amendments`;
  }

  if (fails === 0 && partialFails > 0) {
    return `Partially failed to apply ${partialFails} Amendments`;
  }

  return `Failed to apply ${fails + partialFails} Amendments`;
}

function getIcon(isApplying, failCount, partialFailCount, allSuccess) {
  if (isApplying) {
    return <CircularProgress size="16px" />;
  }

  if (allSuccess) {
    return <CheckIcon style={{color: "green"}} />;
  }

  if (failCount === 0 && partialFailCount > 1) {
    return <WarningIcon style={{color: "orange"}} />;
  }

  return <ErrorOutlineIcon style={{color: "red"}} />;
}

const PartialFailDetail: React.FC<{failSubHistory: AmendmentHistory[]}> = ({
  failSubHistory,
}) => {
  const [expanded, setExpanded] = useState({});

  const toggle = idx => {
    setExpanded(prev => ({
      ...prev,
      [idx]: !prev[idx],
    }));
  };

  return (
    <>
      <div
        style={{
          fontSize: "0.8em",
          color: "rgb(102, 102, 102)",
        }}
      >
        <span>Parts of the amendment failed to apply:</span>
        <ul>
          {failSubHistory.map((item, idx) => {
            const text = item.reason ?? "";
            const isExpanded = expanded[idx];

            return (
              <li
                key={`fail-item-${idx}`}
                onClick={() => toggle(idx)}
                style={{cursor: "pointer"}}
              >
                {isExpanded ? text : `${text.slice(0, 100)}... [Show more]`}
              </li>
            );
          })}
        </ul>
      </div>
    </>
  );
};

const AmendmentDetail: React.FC<{
  amendment: DocumentAmendment;
  hasFailed: boolean;
  subHistory?: AmendmentHistory[];
  onScrollToClause?: (amendment: DocumentAmendment) => void;
}> = ({amendment, hasFailed, subHistory, onScrollToClause}) => {
  const isPartialFailure =
    subHistory !== undefined &&
    (subHistory ?? []).some(history => history.hasFailed);
  const handleChange = (_event, expanded: boolean) => {
    if (expanded && onScrollToClause) {
      onScrollToClause(amendment);
    }
  };
  return (
    <Accordion onChange={handleChange}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <div style={{display: "flex", gap: "0.5em", alignItems: "center"}}>
          {hasFailed && !isPartialFailure ? (
            <CrossIcon style={{color: "red"}} />
          ) : hasFailed && isPartialFailure ? (
            <WarningIcon style={{color: "orange"}} />
          ) : (
            <CheckIcon style={{color: "green"}} />
          )}
          <Typography component="span">
            {amendment.clause_reference
              ? `Clause ${amendment.clause_reference} `
              : ""}
            {_.capitalize(amendment.action_type.replace("_", " "))}
          </Typography>
        </div>
      </AccordionSummary>
      <AccordionDetails style={{flexDirection: "column", gap: "0.5em"}}>
        <span>{amendment.instruction}</span>
        {isPartialFailure && <PartialFailDetail failSubHistory={subHistory} />}
        <span
          style={{
            fontSize: "0.8em",
            fontStyle: "italic",
            color: "rgb(102, 102, 102)",
          }}
        >
          {amendment.rationale}
        </span>
      </AccordionDetails>
    </Accordion>
  );
};

const AmendmentsReport: React.FC<{
  amendmentsHistory: AmendmentHistory[];
  isApplying: boolean;
  totalAmendments: number;
  onScrollToClause?: (amendment: DocumentAmendment) => void;
}> = ({amendmentsHistory, totalAmendments, isApplying, onScrollToClause}) => {
  const failedAmendments = amendmentsHistory.filter(({hasFailed}) => hasFailed);
  const successfulAmendments = amendmentsHistory.filter(
    ({hasFailed}) => !hasFailed,
  );

  const failCount = failedAmendments.filter(
    item => item.subHistory === undefined,
  ).length;
  const partialFailCount = failedAmendments.filter(
    item => item.subHistory !== undefined,
  ).length;
  const allSuccess = failedAmendments.length === 0;
  const [showAll, setShowAll] = useState(false);

  const displayAmendments = allSuccess
    ? amendmentsHistory
    : [...failedAmendments, ...(showAll ? successfulAmendments : [])];

  const handleShowAll = event => {
    setShowAll(event.target.checked);
  };

  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <div style={{display: "flex", gap: "0.5em", alignItems: "center"}}>
          {getIcon(isApplying, failCount, partialFailCount, allSuccess)}
          <span>
            {getMessage(
              isApplying,
              amendmentsHistory.length,
              failCount,
              partialFailCount,
              totalAmendments,
            )}
          </span>
        </div>
      </AccordionSummary>
      <div>
        {displayAmendments.map(({amendment, hasFailed, subHistory}, idx) => {
          return (
            <AmendmentDetail
              key={`amendment-report-${idx}`}
              amendment={amendment}
              hasFailed={hasFailed}
              subHistory={subHistory}
              onScrollToClause={onScrollToClause}
            />
          );
        })}
        {!allSuccess && (
          <FormGroup style={{padding: "12px 16px"}}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={showAll}
                  onChange={handleShowAll}
                  size="small"
                />
              }
              label={"Show All"}
            />
          </FormGroup>
        )}
      </div>
    </Accordion>
  );
};

export default AmendmentsReport;
